import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiErrorMessage, bookingPageApi, type BookingPageAdmin } from './api';
import { EmbedCard } from './components/EmbedCard';

const QUERY_KEY = ['admin-booking-page'];

// The snippet options are read from the card itself, so the allowed origins
// are the only thing to store: adding or removing one saves right away and
// this panel needs no save bar.
export function EmbedSettings() {
  const { t } = useTranslation('adminBookingPage');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: QUERY_KEY, queryFn: bookingPageApi.get });

  const save = useMutation({
    mutationFn: (allowed_origins: string[]) => bookingPageApi.update({ embed: { allowed_origins } }),
    // Show the chip right away, put the old list back if the save fails.
    onMutate: async (allowed_origins: string[]) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<BookingPageAdmin>(QUERY_KEY);
      queryClient.setQueryData<BookingPageAdmin>(QUERY_KEY, (old) =>
        old && { ...old, settings: { ...old.settings, embed: { allowed_origins } } });
      return { previous };
    },
    onSuccess: (next: BookingPageAdmin) => {
      queryClient.setQueryData(QUERY_KEY, next);
      toast({ title: t('save.saved') });
    },
    onError: (error, _origins, context) => {
      if (context?.previous) queryClient.setQueryData(QUERY_KEY, context.previous);
      toast({ title: apiErrorMessage(error, t('save.failed')), variant: 'destructive' });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  return (
    <div data-testid="embed-settings">
      <EmbedCard
        subdomain={data.organization.subdomain}
        orgName={data.organization.name}
        allowedOrigins={data.settings.embed.allowed_origins}
        onAllowedOrigins={(allowed_origins) => save.mutate(allowed_origins)}
      />
    </div>
  );
}
